import { useState, useEffect, useCallback, useRef } from 'react';
import {
  buscarDashboardDetalhado,
  // Novas funções do cache histórico - ETAPA 5
  buscarDadosHistoricos,
  verificarStatusCache,
  buscarDadosConsolidados,
  listarMesesDisponiveis,
  healthCheckCache
} from 'features/financeiro/services/financeiroService';
import financeiroConsolidadoService from 'features/financeiro/services/financeiroConsolidadoService';
import { getCompanyContext } from 'utils/setCompanyContext';
import { useDeduplicatedRequest } from 'hooks/useDeduplicatedRequest';

/**
 * Hook personalizado para gerenciar dados financeiros com renderização incremental
 * Centraliza a lógica de carregamento, estados e atualizações
 * Inclui funcionalidades do cache histórico - ETAPA 5
 * 
 * Renderização incremental:
 * - Carrega dados históricos primeiro (mais importante)
 * - Carrega status, consolidados e meses em paralelo
 * - Atualiza progresso em tempo real
 * - Melhora percepção de velocidade
 */
export const useFinanceiro = () => {
  // Estados existentes
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dadosFinanceiros, setDadosFinanceiros] = useState(null);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(false); // Controle de carregamento
  const [initialLoading, setInitialLoading] = useState(true); // Loading inicial da página
  const loadingRef = useRef(loading);
  const isLoadingDataRef = useRef(isLoadingData);
  const lastRequestRef = useRef({
    paramsDate: null,
    filtros: { tipo_financeiro: 'todos' }
  });
  
  // Hook para deduplicação de requisições
  const { executeRequest } = useDeduplicatedRequest();
  
  // Estados para modo consolidado
  const [isConsolidated, setIsConsolidated] = useState(false);
  const [dadosConsolidados, setDadosConsolidados] = useState(null);
  const [loadingConsolidado, setLoadingConsolidado] = useState(false);
  const [errorConsolidado, setErrorConsolidado] = useState(null);

  // ========================================
  // NOVOS ESTADOS DO CACHE HISTÓRICO - ETAPA 5
  // ========================================
  const [dadosHistoricos, setDadosHistoricos] = useState(null);
  const [statusCache, setStatusCache] = useState(null);
  const [dadosConsolidadosCache, setDadosConsolidadosCache] = useState(null);
  const [mesesDisponiveis, setMesesDisponiveis] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [errorHistorico, setErrorHistorico] = useState(null);
  
  // Estados para renderização incremental
  const [progressoHistorico, setProgressoHistorico] = useState({
    dadosHistoricos: false,
    statusCache: false,
    dadosConsolidados: false,
    mesesDisponiveis: false
  });
  const [loadingIncremental, setLoadingIncremental] = useState(false);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    isLoadingDataRef.current = isLoadingData;
  }, [isLoadingData]);

  const carregarDados = useCallback(async (paramsDate = null, filtros = {}) => {
    lastRequestRef.current = {
      paramsDate,
      filtros: {
        tipo_financeiro: filtros.tipo_financeiro || 'todos'
      }
    };

    
    // Evitar múltiplas chamadas simultâneas
    if (loadingRef.current || isLoadingDataRef.current) {
      return;
    }
    
    setIsLoadingData(true);
    setLoading(true);
    setError(null);
    setErrorConsolidado(null);
    
    try {
      // Obter contexto da empresa selecionada
      const companyContext = getCompanyContext();
      
      // Para teste: forçar modo consolidado se houver parâmetro na URL
      const urlParams = new URLSearchParams(window.location.search);
      const forceConsolidated = urlParams.get('consolidated') === 'true';
      
      const isConsolidatedValue = forceConsolidated || 
                                companyContext?.isConsolidated || 
                                companyContext?.isGroup || 
                                (companyContext?.companies?.length > 1);
      
      setIsConsolidated(isConsolidatedValue);
      
      // Preparar parâmetros de data
      let dataInicio, dataFim;
      if (paramsDate && paramsDate.start && paramsDate.end) {
        dataInicio = paramsDate.start;
        dataFim = paramsDate.end;
      } else {
        // Padrão: mês atual
        const hoje = new Date();
        dataInicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0];
        dataFim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0];
      }
      
      if (isConsolidatedValue) {
        // Modo consolidado - buscar dados de múltiplas empresas
        setLoadingConsolidado(true);
        let companyNames;
        
        if (companyContext?.companies?.length > 0) {
          // Incluir todas as empresas do contexto (incluindo MOV Fit)
          companyNames = companyContext.companies.map(company => company.name);
        }
        // Sem empresas no contexto: não usar fallback e não disparar requests
        if (!companyNames || !companyNames.length) {
          setDadosConsolidados(null);
          setDadosFinanceiros(null);
          setLoadingConsolidado(false);
          return;
        }
        
        const dadosConsolidados = await financeiroConsolidadoService.buscarDadosConsolidados(
          companyNames,
          {
            data_inicio: dataInicio,
            data_fim: dataFim,
            tipo_financeiro: filtros.tipo_financeiro || 'todos'
          }
        );
        
        if (dadosConsolidados.success && dadosConsolidados.data) {
          setDadosConsolidados(dadosConsolidados.data);
          setDadosFinanceiros(dadosConsolidados.data); // Para compatibilidade
          setUltimaAtualizacao(new Date());
        } else {
          throw new Error('Erro ao carregar dados financeiros consolidados');
        }
        
        setLoadingConsolidado(false);
      } else {
        // Modo individual - buscar dados de uma empresa específica
        const empresa = companyContext?.companies?.[0]?.name || 'MOV Fibra';
        const tipoFinanceiro = filtros.tipo_financeiro || 'todos';
        
        const dashboardResponse = await executeRequest(
          buscarDashboardDetalhado,
          `dashboard-detalhado-${empresa}-${dataInicio}-${dataFim}-${tipoFinanceiro}`,
          { 
            empresa: empresa,
            data_inicio: dataInicio,
            data_fim: dataFim,
            tipo_financeiro: tipoFinanceiro
          },
          'POST',
          90000 // 90 segundos de timeout (alinhado com backend)
        );
                

        // Verificar se a requisição foi cancelada/obsoleta
        if (!dashboardResponse) {
          // REMOVIDO: Retry automático que causava loop infinito
          return;
        }

        if (dashboardResponse.success && dashboardResponse.data) {
          // Buscar histórico de fluxo de caixa separadamente (sempre mês atual, ignorando filtro)
          try {
            const hoje = new Date();
            const mesAtualInicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1).toISOString().split('T')[0];
            const mesAtualFim = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0).toISOString().split('T')[0];
            
            const fluxoCaixaResponse = await executeRequest(
              buscarDashboardDetalhado,
              `dashboard-detalhado-fluxo-caixa-${empresa}-${mesAtualInicio}-${mesAtualFim}-${tipoFinanceiro}`,
              { 
                empresa: empresa,
                data_inicio: mesAtualInicio,
                data_fim: mesAtualFim,
                tipo_financeiro: tipoFinanceiro
              },
              'POST',
              30000
            );

            // Se a resposta contiver histórico de fluxo de caixa, usar esse histórico
            if (fluxoCaixaResponse && fluxoCaixaResponse.success && fluxoCaixaResponse.data) {
              let fluxoHistorico = null;
              
              // Tentar diferentes estruturas de resposta da API
              if (Array.isArray(fluxoCaixaResponse.data)) {
                const primeiroItem = fluxoCaixaResponse.data[0];
                if (primeiroItem && primeiroItem.fluxoCaixaHistorico) {
                  fluxoHistorico = primeiroItem.fluxoCaixaHistorico;
                } else if (primeiroItem && primeiroItem.data && primeiroItem.data.fluxoCaixaHistorico) {
                  fluxoHistorico = primeiroItem.data.fluxoCaixaHistorico;
                }
              } else if (fluxoCaixaResponse.data.fluxoCaixaHistorico) {
                fluxoHistorico = fluxoCaixaResponse.data.fluxoCaixaHistorico;
              } else if (fluxoCaixaResponse.data.data && fluxoCaixaResponse.data.data.fluxoCaixaHistorico) {
                fluxoHistorico = fluxoCaixaResponse.data.data.fluxoCaixaHistorico;
              }
              
              // Atualizar os dados principais com o histórico correto (sempre mês atual)
              if (fluxoHistorico && Array.isArray(fluxoHistorico)) {
                // Garantir que seja um array no nível correto dos dados principais
                if (Array.isArray(dashboardResponse.data)) {
                  // Se dados é array, atualizar o primeiro item se existir
                  if (dashboardResponse.data[0]) {
                    dashboardResponse.data[0].fluxoCaixaHistorico = fluxoHistorico;
                    // Também adicionar no nível raiz para garantir compatibilidade
                    dashboardResponse.data.fluxoCaixaHistorico = fluxoHistorico;
                  } else {
                    dashboardResponse.data.fluxoCaixaHistorico = fluxoHistorico;
                  }
                } else if (dashboardResponse.data && typeof dashboardResponse.data === 'object') {
                  // Se dados é objeto, adicionar diretamente
                  dashboardResponse.data.fluxoCaixaHistorico = fluxoHistorico;
                  // Também verificar se tem estrutura data[0]
                  if (dashboardResponse.data.data && Array.isArray(dashboardResponse.data.data) && dashboardResponse.data.data[0]) {
                    dashboardResponse.data.data[0].fluxoCaixaHistorico = fluxoHistorico;
                  }
                } else {
                  // Criar estrutura se necessário
                  dashboardResponse.data = {
                    ...dashboardResponse.data,
                    fluxoCaixaHistorico: fluxoHistorico
                  };
                }
              }
            }
          } catch (error) {
            console.warn('⚠️ Erro ao buscar histórico de fluxo de caixa (continuando com dados principais):', error);
            // Continuar mesmo se falhar - usar dados que vieram da primeira chamada
          }
          
          setDadosFinanceiros(dashboardResponse.data);
          setDadosConsolidados(null); // Limpar dados consolidados
          setUltimaAtualizacao(new Date());
        } else {
          throw new Error('Erro ao carregar dados financeiros');
        }
      }
    } catch (error) {
      console.error('❌ Erro ao carregar dados financeiros:', error);
      setError('Não foi possível carregar os dados financeiros. Verifique sua conexão e tente novamente.');
    } finally {
      setLoading(false);
      setIsLoadingData(false);
      setInitialLoading(false);
      // Disparar evento quando loading termina
      window.dispatchEvent(new CustomEvent('company-change-complete'));
    }
  }, [executeRequest]);

  // ========================================
  // NOVAS FUNÇÕES DO CACHE HISTÓRICO - ETAPA 5
  // ========================================

  /**
   * Carregar dados históricos do cache
   * @param {Object} params - Parâmetros da busca
   * @param {number} params.meses - Número de meses a buscar (padrão: 11)
   * @param {string} params.empresa - Nome da empresa (opcional)
   */
  const carregarDadosHistoricos = useCallback(async (params = {}, isIncremental = false) => {
    if (!isIncremental) {
      setLoadingHistorico(true);
    }
    setErrorHistorico(null);
    
    try {
      const resultado = await executeRequest(
        buscarDadosHistoricos,
        `dados-historicos-${JSON.stringify(params)}`,
        params,
        'POST',
        30000
      );
      
      if (resultado.success) {
        setDadosHistoricos(resultado.data);
        if (isIncremental) {
          setProgressoHistorico(prev => ({ ...prev, dadosHistoricos: true }));
        }
      } else {
        throw new Error(resultado.error || 'Erro ao carregar dados históricos');
      }
    } catch (error) {
      console.error('❌ Erro ao carregar dados históricos:', error);
      setErrorHistorico(error.message || 'Erro ao carregar dados históricos');
    } finally {
      if (!isIncremental) {
        setLoadingHistorico(false);
      }
    }
  }, [executeRequest]);

  /**
   * Verificar status do cache histórico
   */
  const verificarStatus = useCallback(async (isIncremental = false) => {
    try {
      const resultado = await executeRequest(
        verificarStatusCache,
        'verificar-status-cache',
        {},
        'POST',
        30000
      );
      
      if (resultado.success) {
        setStatusCache(resultado.data);
        if (isIncremental) {
          setProgressoHistorico(prev => ({ ...prev, statusCache: true }));
        }
      } else {
        console.warn('⚠️ Erro ao verificar status do cache:', resultado.error);
      }
    } catch (error) {
      console.error('❌ Erro ao verificar status do cache:', error);
    }
  }, [executeRequest]);

  /**
   * Carregar dados consolidados
   */
  const carregarDadosConsolidados = useCallback(async (isIncremental = false) => {
    try {
      const resultado = await executeRequest(
        buscarDadosConsolidados,
        'dados-consolidados-cache',
        {},
        'POST',
        30000
      );
      
      if (resultado.success) {
        setDadosConsolidadosCache(resultado.data);
        if (isIncremental) {
          setProgressoHistorico(prev => ({ ...prev, dadosConsolidados: true }));
        }
      } else {
        console.warn('⚠️ Erro ao carregar dados consolidados:', resultado.error);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar dados consolidados:', error);
    }
  }, [executeRequest]);

  /**
   * Carregar meses disponíveis
   */
  const carregarMesesDisponiveis = useCallback(async (isIncremental = false) => {
    try {
      const resultado = await listarMesesDisponiveis();
      
      if (resultado.success) {
        setMesesDisponiveis(resultado.data);
        if (isIncremental) {
          setProgressoHistorico(prev => ({ ...prev, mesesDisponiveis: true }));
        }
      } else {
        console.warn('⚠️ Erro ao carregar meses disponíveis:', resultado.error);
      }
    } catch (error) {
      console.error('❌ Erro ao carregar meses disponíveis:', error);
    }
  }, []);

  /**
   * Health check do cache
   */
  const healthCheck = useCallback(async () => {
    try {
      const resultado = await healthCheckCache();
      
      if (resultado.success) {
        // Atualizar status do cache após health check
        await verificarStatus();
      } else {
        console.warn('⚠️ Health check do cache falhou:', resultado.error);
      }
    } catch (error) {
      console.error('❌ Erro no health check do cache:', error);
    }
  }, [verificarStatus]);

  /**
   * Carregar todos os dados históricos com renderização incremental
   */
  const carregarTodosDadosHistoricos = useCallback(async () => {
    setLoadingIncremental(true);
    setErrorHistorico(null);
    
    // Resetar progresso
    setProgressoHistorico({
      dadosHistoricos: false,
      statusCache: false,
      dadosConsolidados: false,
      mesesDisponiveis: false
    });
    
    try {
      // Carregar dados históricos primeiro (mais importante)
      await carregarDadosHistoricos({ meses: 12 }, true);
      
      // Carregar status do cache em paralelo
      verificarStatus(true);
      
      // Carregar dados consolidados em paralelo
      carregarDadosConsolidados(true);
      
      // Carregar meses disponíveis por último
      carregarMesesDisponiveis(true);
      
    } catch (error) {
      console.error('❌ Erro ao carregar todos os dados históricos:', error);
      setErrorHistorico('Erro ao carregar dados históricos: ' + error.message);
    } finally {
      // Finalizar loading após um delay para permitir renderização incremental
      setTimeout(() => {
        setLoadingIncremental(false);
      }, 200);
    }
  }, [carregarDadosHistoricos, carregarDadosConsolidados, carregarMesesDisponiveis, verificarStatus]);

  /**
   * Limpar erro histórico
   */
  const limparErroHistorico = useCallback(() => {
    setErrorHistorico(null);
  }, []);

  // Monitorar progresso da renderização incremental
  useEffect(() => {
    if (loadingIncremental) {
      const todasCompletas = Object.values(progressoHistorico).every(completo => completo);
      if (todasCompletas) {
        setLoadingIncremental(false);
      }
    }
  }, [progressoHistorico, loadingIncremental]);

  // ========================================
  // FUNÇÕES EXISTENTES
  // ========================================

  const atualizarDados = useCallback(async (paramsDate = null, filtros = {}) => {
    await carregarDados(paramsDate, filtros);
  }, [carregarDados]);

  // Carregar dados iniciais e reagir a mudanças de empresa
  useEffect(() => {
    carregarDados(null, { tipo_financeiro: 'todos' });
    
    // Listener para mudanças de empresa com debounce
    let timeoutId;
    const handleCompanyChange = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        carregarDados(
          lastRequestRef.current?.paramsDate || null,
          lastRequestRef.current?.filtros || { tipo_financeiro: 'todos' }
        );
      }, 100); // Debounce de 100ms
    };
    
    window.addEventListener('company-changed', handleCompanyChange);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('company-changed', handleCompanyChange);
    };
  }, [carregarDados]); // Mantém bootstrap estável com callback memoizado

  return {
    // Estados existentes
    loading,
    initialLoading,
    error,
    dadosFinanceiros,
    ultimaAtualizacao,
    atualizarDados,
    
    // Estados para modo consolidado
    isConsolidated,
    dadosConsolidados,
    loadingConsolidado,
    errorConsolidado,
    
    // Novos estados do cache histórico
    dadosHistoricos,
    statusCache,
    dadosConsolidadosCache,
    mesesDisponiveis,
    loadingHistorico,
    errorHistorico,
    
    // Estados para renderização incremental
    loadingIncremental,
    progressoHistorico,
    
    // Novas funções do cache histórico
    carregarDadosHistoricos,
    verificarStatus,
    carregarDadosConsolidados,
    carregarMesesDisponiveis,
    healthCheck,
    carregarTodosDadosHistoricos,
    limparErroHistorico
  };
};

export default useFinanceiro;
